import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import AppButton from '../../../../shared/components/app-button/AppButton';
import CruiseLayout from '../../components/common/CruiseLayout';
import {
  CRUISE_ROUTES,
  TITLE_OPTIONS,
  WALLET_MODE,
} from '../../constants/cruiseConstants';
import { useCruiseFlow } from '../../utils/cruiseFlow';
import placeholder from '../../assets/cruise-placeholder.jpg';
import {
  bedLabel,
  cabinShortLabel,
  cruiseTitle,
  diningLabel,
  formatAmount,
  formatDateRange,
  formatDay,
  readInvoice,
  titleCase,
} from '../../utils/cruiseUtils';

const TYPE_LABEL = { adult: 'Adult', child: 'Child', infant: 'Infant' };

const titleLabel = (code) =>
  [...TITLE_OPTIONS.adult, ...TITLE_OPTIONS.child].find((t) => t.value === code)
    ?.label || code;

function CruiseConfirmationPage() {
  const navigate = useNavigate();
  const flow = useCruiseFlow();
  const { cruise, category, cabin, bed, dining, priceCheck, booking, guests } =
    flow;
  const ready = Boolean(cruise && booking?.ref);

  useEffect(() => {
    if (!ready) navigate(CRUISE_ROUTES.LANDING, { replace: true });
  }, [ready, navigate]);

  if (!ready) return null;

  const invoice = readInvoice(priceCheck?.invoice);
  const lead = guests?.list?.[0];
  const finalDue = invoice?.schedule?.[0]?.dueDate;

  const newSearch = () => {
    navigate(CRUISE_ROUTES.LANDING);
  };

  return (
    <CruiseLayout step="done" narrow>
      <div className="cr-confirmation-actions">
        <AppButton
          btnText="Print"
          variant="outlined"
          onClick={() => window.print()}
        />
        <AppButton
          btnText="Home"
          variant="outlined"
          onClick={() => navigate('/')}
        />
      </div>

      <div className="cr-panel cr-done">
        <span className="cr-done__icon">
          <FiCheck size={28} strokeWidth={2.6} />
        </span>
        <h1 className="cr-done__title">Booking confirmed</h1>
        <div className="cr-done__sub">
          {booking.amount > 0 && `Paid ${formatAmount(booking.amount)}`}
          {booking.payMode === WALLET_MODE && booking.amount > 0
            ? ' from wallet'
            : ''}
          {booking.amount > 0 ? '. ' : ''}
          {lead?.email &&
            `A confirmation will be sent to ${lead.email.trim()}.`}
        </div>
        <div className="cr-done__ref">
          <span className="cr-muted-sm">Booking reference</span>
          <b>{booking.ref}</b>
        </div>
        <div className="cr-done__actions">
          <AppButton
            btnText="Book Another Cruise"
            variant="outlined"
            onClick={newSearch}
            style={{ height: '42px', padding: '0 18px', fontWeight: 600 }}
          />
        </div>
      </div>

      <div className="cr-hero cr-hero--compact">
        <img className="cr-hero__img" src={placeholder} alt="" />
        <div className="cr-hero__body">
          <div className="cr-meta">
            {[cruise.cruiseLineName, cruise.shipName]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <b className="cr-card__title">{cruiseTitle(cruise.cruiseName)}</b>
          <div className="cr-facts cr-facts--sm">
            <div>
              <span>Sailing</span>
              <b>{formatDateRange(cruise.startDate, cruise.endDate)}</b>
            </div>
            <div>
              <span>Embarkation</span>
              <b>{titleCase(cruise.startPortName)}</b>
            </div>
            {category && cabin && (
              <div>
                <span>Cabin</span>
                <b>
                  {category.categoryCode} · {cabinShortLabel(cabin)}
                </b>
              </div>
            )}
            {bed && (
              <div>
                <span>Bed</span>
                <b>{bedLabel(bed)}</b>
              </div>
            )}
            {dining && (
              <div>
                <span>Dining</span>
                <b>{diningLabel(dining)}</b>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cr-done__grid">
        {guests?.list?.length > 0 && (
          <div className="cr-panel cr-panel--pad">
            <b className="cr-h4">Guests</b>
            {guests.list.map((g, i) => (
              <div
                className="cr-kv__row cr-kv__row--line"
                // eslint-disable-next-line react/no-array-index-key
                key={i}
              >
                <span>
                  <b>
                    {[titleLabel(g.salutation), g.firstName, g.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </b>{' '}
                  <span className="cr-muted-sm">· {TYPE_LABEL[g.paxType]}</span>
                </span>
                <span className="cr-muted-sm">{g.passportNumber || '—'}</span>
              </div>
            ))}
          </div>
        )}
        {invoice && (
          <div className="cr-panel cr-panel--pad">
            <b className="cr-h4">Payment</b>
            <div className="cr-kv__row">
              <span>Total fare</span>
              <b>{formatAmount(invoice.total)}</b>
            </div>
            {invoice.netPayable !== null && (
              <div className="cr-kv__row">
                <span>Net paid by agency</span>
                <b>{formatAmount(invoice.netPayable)}</b>
              </div>
            )}
            {finalDue && (
              <div className="cr-kv__row">
                <span>Final payment due</span>
                <b>{formatDay(finalDue)}</b>
              </div>
            )}
          </div>
        )}
      </div>
    </CruiseLayout>
  );
}

export default CruiseConfirmationPage;
